import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { setCurrentWarehouse, getCurrentWarehouse, getAllRegions } from '../services/warehouseService';

/**
 * WarehouseUrlHandler
 *
 * Reads the leading warehouse segment from the URL path
 * (e.g. /BFI1/picker-eligibilities) and syncs it to localStorage so the
 * rest of the app uses the correct warehouse.
 *
 * Enables deep-linking: a URL like
 *   https://eligibility.us-east-1-prod.picking.aft.amazon.dev/BFI1
 * automatically selects BFI1 without the user needing to use the dropdown.
 *
 * Note: this component is rendered outside <Routes>, so it cannot use
 * useParams() (which only resolves params for components rendered by a
 * matched <Route>). Instead it parses the warehouse ID directly from
 * location.pathname.
 *
 * Fires the `warehouseChanged` event so listening components re-render.
 */
const WarehouseUrlHandler: React.FC = () => {
    const location = useLocation();

    useEffect(() => {
        // Match a leading warehouse segment: 2-5 letters + 1-3 digits
        // (e.g. /BFI1, /DCA2/picker-eligibilities, /IND18/clusters)
        const match = location.pathname.match(/^\/([A-Za-z]{2,5}[0-9]{1,3})(?:\/|$)/);
        if (!match) return;

        const normalizedId = match[1].toUpperCase();
        const current = getCurrentWarehouse();

        // Skip if already set to this warehouse
        if (current.warehouseId === normalizedId) return;

        // Set immediately with what we know — region resolved async below
        setCurrentWarehouse({
            warehouseId: normalizedId,
            regionCode: current.regionCode,
            displayName: normalizedId,
        });

        // Resolve the correct region from the static warehouse list
        getAllRegions().then(regions => {
            for (const region of regions) {
                const matchedWarehouse = region.warehouses.find(
                    w => w.warehouseId.toUpperCase() === normalizedId
                );
                if (matchedWarehouse) {
                    setCurrentWarehouse(matchedWarehouse);
                    break;
                }
            }
        }).catch(() => { /* non-critical — region is best-effort */ });

        window.dispatchEvent(new Event('warehouseChanged'));
    }, [location.pathname]);

    return null;
};

export default WarehouseUrlHandler;
